import type { ConnectionCurve } from "./connectionCurve";

type MovingCurve = {group: SVGGElement; plan: Element; initial: DOMMatrix};

/** Reads only the plan transform, never the individual connection anchors. */
function planMatrix(plan: Element): DOMMatrix | undefined {
  if (plan instanceof SVGSVGElement) return plan.getScreenCTM() ?? undefined;
  if (!(plan instanceof HTMLElement) || !plan.offsetWidth || !plan.offsetHeight) return;
  const bounds = plan.getBoundingClientRect();
  return new DOMMatrix([bounds.width / plan.offsetWidth, 0, 0, bounds.height / plan.offsetHeight, bounds.left, bounds.top]);
}

/** Captures the coordinate system belonging to each already rendered plan connection. */
export function captureConnectionNavigation(svg: SVGSVGElement, curves: readonly ConnectionCurve[]): MovingCurve[] {
  const plans = [...document.querySelectorAll('.C1_planSvg'), ...document.querySelectorAll('.page')];
  const result: MovingCurve[] = [];
  for (const curve of curves) {
    const endpoint = curve.endpoints.find(endpoint => endpoint.surface === 'drawing');
    if (!endpoint) continue;
    const plan = plans.find(plan => {
      const bounds = plan.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 && endpoint.point.clientX >= bounds.left
        && endpoint.point.clientX <= bounds.right && endpoint.point.clientY >= bounds.top && endpoint.point.clientY <= bounds.bottom;
    });
    const group = svg.querySelector<SVGGElement>(`[data-connection-id="${CSS.escape(curve.connectionId)}"]`);
    const initial = plan && planMatrix(plan);
    if (group && plan && initial && initial.a * initial.d - initial.b * initial.c !== 0) result.push({group, plan, initial});
  }
  return result;
}

/** Moves cached SVG paths using one matrix measurement per plan and frame. */
export function transformConnectionNavigation(curves: readonly MovingCurve[]): void {
  const matrices = new Map<Element, DOMMatrix | undefined>();
  for (const curve of curves) {
    if (!matrices.has(curve.plan)) matrices.set(curve.plan, planMatrix(curve.plan));
    const current = matrices.get(curve.plan);
    if (!current) continue;
    const delta = current.multiply(curve.initial.inverse());
    curve.group.setAttribute('transform', `matrix(${delta.a} ${delta.b} ${delta.c} ${delta.d} ${delta.e} ${delta.f})`);
  }
}

/** Drops the temporary transform when freshly calculated paths replace the cached ones. */
export function clearConnectionNavigation(curves: readonly MovingCurve[]): void {
  for (const curve of curves) curve.group.removeAttribute('transform');
}
